/**
 * Eine Zeile der Übertragungshistorie: was übertragen wurde, für welchen Zeitraum, mit welchen
 * Werten und was Channex bei der Rückprüfung bestätigt hat. Wortwahl in
 * `pushHistoryPresentation`, Wertepillen in `PushHistoryValuePills`.
 */
import { Ban, CheckCircle2, CircleDashed, RefreshCcw, Tag, TriangleAlert } from 'lucide-react';
import type { AriPushHistoryItem } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { formatDateRange, formatRelative } from '@/shared/lib/format';
import { PushHistoryValuePills } from './PushHistoryValuePills';
import { TaskIdChips } from './TaskIdChips';
import {
  nightsText,
  pushHeadline,
  readbackStatus,
  type ReadbackTone,
} from './pushHistoryPresentation';

interface Props {
  item: AriPushHistoryItem;
  currency: string;
  onReapply?: ((item: AriPushHistoryItem) => void) | undefined;
  isFirst?: boolean;
}

const STATUS_STYLE: Record<ReadbackTone, { icon: typeof CheckCircle2; className: string }> = {
  confirmed: { icon: CheckCircle2, className: 'text-sage' },
  drift: { icon: TriangleAlert, className: 'text-amber' },
  unchecked: { icon: CircleDashed, className: 'text-ink-faint' },
};

export function PushHistoryRow({ item, currency, onReapply, isFirst }: Props) {
  const overridden = item.effectiveDayCount === 0;
  const partly = item.effectiveDayCount > 0 && item.effectiveDayCount < item.daysCount;
  const blocking = item.stopSell === true || item.available === false;
  const status = readbackStatus(item);
  const { icon: StatusIcon, className: statusClass } = STATUS_STYLE[status.tone];
  const range = formatDateRange(item.dateFrom, item.dateTo);

  return (
    <li className={cn('flex items-start gap-3 py-4', !isFirst && 'border-line/40 border-t')}>
      <span
        aria-hidden
        className={cn(
          'mt-0.5 grid size-9 shrink-0 place-items-center rounded-md',
          blocking ? 'bg-sealwax/15 text-sealwax' : 'bg-sage/15 text-sage',
        )}
      >
        {blocking ? <Ban size={16} strokeWidth={2} /> : <Tag size={16} strokeWidth={2} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <span className="text-ink font-serif text-[15px] font-medium">{pushHeadline(item)}</span>
          <span className="text-ink-faint text-[11px]">{formatRelative(item.pushedAt)}</span>
        </div>
        <p className="text-ink-soft mt-0.5 text-[13px]">
          {range} · {nightsText(item.daysCount)}
          {partly && (
            <span className="text-ink-faint"> · {item.effectiveDayCount} davon noch wirksam</span>
          )}
          {overridden && <span className="text-ink-faint"> · vollständig überschrieben</span>}
        </p>
        <div className="mt-2">
          <PushHistoryValuePills item={item} currency={currency} />
        </div>
        <p
          className={cn('mt-2 flex items-center gap-1.5 text-[12px]', statusClass)}
          title={status.detail}
        >
          <StatusIcon size={13} aria-hidden />
          {status.label}
          {item.warnings.length > 0 && (
            <span className="text-amber" title={item.warnings.join('\n')}>
              · {item.warnings.length === 1 ? '1 Hinweis' : `${item.warnings.length} Hinweise`} von
              Channex
            </span>
          )}
        </p>
        {item.taskIds.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px]">
            <TaskIdChips taskIds={item.taskIds} item={item} />
          </div>
        )}
      </div>
      {onReapply && (
        <button
          type="button"
          onClick={() => onReapply(item)}
          aria-label={`${range} erneut bearbeiten`}
          className="text-ink-soft hover:bg-card-alt hover:text-ink inline-flex min-h-11 shrink-0 items-center gap-1 rounded-md px-2 text-[12px] font-medium transition-colors"
        >
          <RefreshCcw size={12} strokeWidth={2} aria-hidden />
          Erneut
        </button>
      )}
    </li>
  );
}
